import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  List, Calendar, User, Users, MoreVertical,
  LayoutGrid, CheckCircle2, PlayCircle, Circle, AlertTriangle,
  SlidersHorizontal, ChevronDown, FolderOpen, FileText,
} from 'lucide-react';
import CreateTaskModal from '../../components/workspace/task/CreateTaskModal';
import TaskGantt from '../../components/workspace/task/TaskGantt';
import TaskSourceTag from '../../components/workspace/task/TaskSourceTag';
import { useApi } from '../../lib/api';
import { useWorkspace } from '../../lib/workspace';
import { useReportScope } from '../../lib/useReportScope';
import { formatHoursMinutes, parseServerDate, formatDate } from '../../lib/format';
import {
  SearchableSelect, FilterDropdown, MultiFilterDropdown,
  DateRangeFilterDropdown, DATE_PRESETS_NO_OVERDUE, DATE_PRESETS_PAST,
} from '../../components/workspace/filters/FilterDropdowns';

const STATUS_LABEL = {
  open:        'Not Started',
  in_progress: 'In Progress',
  blocked:     'Blocked',
  done:        'Completed',
  cancelled:   'Cancelled',
};

const PRIORITY_ORDER = { urgent: 0, critical: 0, high: 1, medium: 2, low: 3 };
const PRIORITY_LABEL = { urgent: 'Urgent', critical: 'Urgent', high: 'High', medium: 'Medium', low: 'Low' };

// Not Started / In Progress first, Blocked/Cancelled next, Completed last
const STATUS_SORT_TIER = { open: 0, in_progress: 0, blocked: 1, cancelled: 1, done: 2 };

const PRIORITY_OPTIONS = [
  { value: 'urgent', label: 'Urgent', dot: '#dc2626' },
  { value: 'high',   label: 'High',   dot: '#f97316' },
  { value: 'medium', label: 'Medium', dot: '#eab308' },
  { value: 'low',    label: 'Low',    dot: '#22c55e' },
];

const STATUS_OPTIONS = [
  { value: 'open',        label: 'Not Started', dot: '#94a3b8' },
  { value: 'in_progress', label: 'In Progress', dot: '#0969a8' },
  { value: 'blocked',     label: 'Blocked',     dot: '#842029' },
  { value: 'done',        label: 'Completed',   dot: '#166534' },
  { value: 'cancelled',   label: 'Cancelled',   dot: '#94a3b8' },
];

const PAGE_SIZE = 20;

/** Format hours cleanly: 5.0 â†' "5", 5.5 â†' "5.5" */
function fmtH(n) {
  const v = parseFloat(n) || 0;
  return v % 1 === 0 ? String(Math.round(v)) : v.toFixed(1);
}

function nameInitials(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

function smartDueLabel(dueOn, status, completedAt) {
  if (status === 'done') {
    return completedAt ? `Completed ${formatDate(completedAt)}` : (dueOn ? formatDate(dueOn) : null);
  }
  if (!dueOn) return null;
  const d = parseServerDate(dueOn);
  if (!d) return formatDate(dueOn);
  const now   = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const due   = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((due - today) / 86400000);

  if (status === 'cancelled') return formatDate(dueOn);
  if (diffDays === 0)  return 'Today';
  if (diffDays === 1)  return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays < 0)   return `${Math.abs(diffDays)}d overdue`;
  if (diffDays <= 7)  return `${diffDays}d left`;
  return formatDate(dueOn);
}

function dueDateClass(dueOn, status) {
  if (!dueOn || status === 'done' || status === 'cancelled') return '';
  const d = parseServerDate(dueOn);
  if (!d) return '';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diff = Math.round((due - today) / 86400000);
  if (diff < 0)  return 'tl-due-overdue';
  if (diff <= 2) return 'tl-due-soon';
  return '';
}

// Module-level: survives component remounts, cleared on page refresh.
// Populated only when navigating into a task detail — so filters restore
// when the user goes back, but reset when they navigate to another section.
let _filterCache = null;

export default function Tasks() {
  const { appId } = useParams();
  const navigate  = useNavigate();
  const location  = useLocation();
  const api       = useApi();
  const { me, canWrite, myRole, searchQuery } = useWorkspace();

  const [tasks, setTasks]             = useState([]);
  const [selectMode, setSelectMode]   = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkCompleting, setBulkCompleting] = useState(false);
  const [bulkStatus, setBulkStatus]   = useState('done');
  const [members, setMembers]         = useState([]);
  const [roles, setRoles]             = useState([]);
  const [areasByRole, setAreasByRole] = useState(new Map());
  const [allAreas, setAllAreas]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);

  const [searchParams, setSearchParams] = useSearchParams();

  const rf = _filterCache?.appId === appId ? _filterCache : null; // restored filters
  const [view,         setView]      = useState(() => rf?.view ?? 'list');
  const statusFilterRaw              = searchParams.get('status');
  const statusFilter = useMemo(
    () => statusFilterRaw ? statusFilterRaw.split(',').filter(Boolean) : [],
    [statusFilterRaw]
  );
  const setStatusFilter = (arr) => setSearchParams(prev => {
    const next = new URLSearchParams(prev);
    if (arr.length) next.set('status', arr.join(',')); else next.delete('status');
    return next;
  }, { replace: false });
  const toggleStatusFilter = (val) => {
    setStatusFilter(statusFilter.includes(val) ? statusFilter.filter(v => v !== val) : [...statusFilter, val]);
  };
  // Arriving from a project/milestone click (ProjectViewModal) should show
  // that project's tasks unfiltered by owner/status — not the "Mine only,
  // Not Started + In Progress" defaults meant for a fresh tab load.
  const fromProject = !!location.state?.projectId;

  // Fresh tab load (no restored filters, no status in URL): default to
  // Not Started + In Progress so the list opens on actionable work.
  useEffect(() => {
    if (!rf && statusFilterRaw === null && !fromProject) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('status', 'open,in_progress');
        return next;
      }, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Deep links (e.g. the owner's daily digest email) can open the list on
  // everyone's tasks (?owner=all) and/or only overdue ones (?deadline=overdue),
  // overriding the restored/default filters.
  const urlOwnerAll       = searchParams.get('owner') === 'all';
  const urlDeadlineOverdue = searchParams.get('deadline') === 'overdue';
  const [ownerFilter,       setOwnerFilter]       = useState(() => urlOwnerAll ? '' : (rf?.ownerFilter ?? (fromProject ? '' : 'mine')));
  const [areaFilter,        setAreaFilter]        = useState(() => rf?.areaFilter        ?? '');
  const [assigneeFilter,    setAssigneeFilter]    = useState(() => rf?.assigneeFilter    ?? '');
  const [needSupportFilter, setNeedSupportFilter] = useState(() => rf?.needSupportFilter ?? false);
  const EMPTY_DATE_FILTER = { preset: '', from: '', to: '' };
  const [deadlineFilter,    setDeadlineFilter]    = useState(() => urlDeadlineOverdue ? { ...EMPTY_DATE_FILTER, preset: 'overdue' } : (rf?.deadlineFilter ?? EMPTY_DATE_FILTER));
  const [createdFilter,     setCreatedFilter]     = useState(() => rf?.createdFilter     ?? EMPTY_DATE_FILTER);
  const [completedFilter,   setCompletedFilter]   = useState(() => rf?.completedFilter   ?? EMPTY_DATE_FILTER);
  const [projectFilter,     setProjectFilter]     = useState(() => rf?.projectFilter     ?? location.state?.projectId ?? '');
  const [projects,          setProjects]          = useState([]);
  const [priorityFilter,    setPriorityFilter]    = useState(() => rf?.priorityFilter    ?? '');
  const [search,          setSearch]          = useState(searchQuery || '');
  const [createOpen,     setCreateOpen]     = useState(false);
  const [editTask,       setEditTask]       = useState(null);
  const [page,           setPage]           = useState(1);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const filterPanelRef = useRef(null);

  const loadStaticContext = useCallback(async () => {
    const [m, r, proj] = await Promise.all([
      api.listAppMembers(appId).catch(() => ({ items: [] })),
      api.roles.list(appId).catch(() => ({ items: [] })),
      api.listProjectsSimple(appId).catch(() => []),
    ]);
    setProjects(proj || []);
    const memberRows = m.items || [];
    const roleRows   = r.items || [];
    setMembers(memberRows);
    setRoles(roleRows);

    const perRole = await Promise.all(
      roleRows.map((role) =>
        api.listAreas(appId, role.id)
          .then((res) => ({ role, items: res.items || [] }))
          .catch(() => ({ role, items: [] }))
      )
    );
    const map  = new Map();
    const flat = [];
    for (const { role, items } of perRole) {
      map.set(role.id, items);
      for (const a of items) flat.push({ ...a, role });
    }
    setAreasByRole(map);
    setAllAreas(flat);
  }, [api, appId]);

  // Load all tasks without status filter — status filtering is client-side
  // so that tab counts are always accurate across all tabs.
  const isAdmin  = myRole === 'super_admin' || myRole === 'admin';
  const isMember = myRole === 'member';
  const { isManager: hasReportees, scopeIds } = useReportScope(appId);


  const loadTasks = useCallback(async () => {
    setError(null);
    try {
      const params = {};
      if (ownerFilter === 'mine')      params.mine   = 1;
      if (areaFilter === 'others')     params.area_id = 'others';
      else if (areaFilter)             params.area_id = areaFilter;
      if (assigneeFilter)              params.assigned_to_user_id = assigneeFilter;
      if (projectFilter)               params.project_id = projectFilter;
      const r = await api.listTasks(appId, params);
      setTasks(r.items || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [api, appId, ownerFilter, areaFilter, assigneeFilter, projectFilter]);

  const prevOwnerFilterRef = useRef(ownerFilter);
  useEffect(() => {
    if (ownerFilter === 'mine' && me) {
      setAssigneeFilter(String(me.id));
    } else if (prevOwnerFilterRef.current === 'mine' && me && assigneeFilter === String(me.id)) {
      setAssigneeFilter('');
    }
    prevOwnerFilterRef.current = ownerFilter;
  }, [ownerFilter, me]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadStaticContext(); }, [loadStaticContext]);
  useEffect(() => { loadTasks(); },        [loadTasks]);
  useEffect(() => { if (searchQuery !== undefined) setSearch(searchQuery); }, [searchQuery]);
  // Clear the cache after it's been consumed on mount so navigating to Home
  // and back won't accidentally restore stale filters.
  useEffect(() => { _filterCache = null; }, []);

  useEffect(() => {
    if (!filterPanelOpen) return;
    const handler = (e) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(e.target)) {
        setFilterPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [filterPanelOpen]);

  const memberName = (id) => {
    if (id == null) return null;
    const u = members.find((m) => m.user_id === id);
    if (!u) return `User #${id}`;
    return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email;
  };

  const memberPhoto = (id) => {
    if (id == null) return null;
    return members.find((m) => m.user_id === id)?.photo_url || null;
  };

  const areaLabel = (areaId) => {
    if (areaId == null) return 'Internal';
    const a = allAreas.find((x) => x.id === areaId);
    if (!a) return `Area #${areaId}`;
    return `${a.role?.title} Â· ${a.name}`;
  };

  const overdueCount = useMemo(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return tasks.filter(t => {
      if (!t.due_on || t.status === 'cancelled') return false;
      const d = parseServerDate(t.due_on);
      if (!d) return false;
      const due = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      if (t.status === 'done') {
        if (!t.completed_at) return false;
        const c = parseServerDate(t.completed_at);
        if (!c) return false;
        const completedDay = new Date(c.getFullYear(), c.getMonth(), c.getDate());
        return completedDay > due;
      }
      return due < today;
    }).length;
  }, [tasks]);

  // ── Summary dashboard (replaces status tabs) ──
  const dashCounts = useMemo(() => {
    let completed = 0, inProgress = 0, notStarted = 0;
    for (const t of tasks) {
      if (t.status === 'done') completed++;
      else if (t.status === 'in_progress') inProgress++;
      else if (t.status === 'open') notStarted++;
    }
    return { total: completed + inProgress + notStarted, completed, inProgress, notStarted, overdue: overdueCount };
  }, [tasks, overdueCount]);

  // Client-side status + deadline + search filter
  const filteredTasks = useMemo(() => {
    const now   = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dayOfWeek     = (today.getDay() + 6) % 7;
    const thisWeekStart = new Date(today); thisWeekStart.setDate(today.getDate() - dayOfWeek);
    const thisWeekEnd   = new Date(thisWeekStart); thisWeekEnd.setDate(thisWeekStart.getDate() + 6);
    const nextWeekStart = new Date(thisWeekStart); nextWeekStart.setDate(thisWeekStart.getDate() + 7);
    const nextWeekEnd   = new Date(nextWeekStart);  nextWeekEnd.setDate(nextWeekStart.getDate() + 6);
    const thisMonthEnd  = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const matchesDateFilter = (dateStr, filter, pastFacing = false) => {
      if (!filter?.preset) return true;
      if (!dateStr) return false;
      const d = parseServerDate(dateStr);
      if (!d) return false;
      const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      switch (filter.preset) {
        case 'today':      return day.getTime() === today.getTime();
        case 'this_week':  return pastFacing
          ? day >= thisWeekStart && day <= today
          : day >= today && day <= thisWeekEnd;
        case 'next_week':  return day >= nextWeekStart && day <= nextWeekEnd;
        case 'this_month': return pastFacing
          ? day >= thisMonthStart && day <= today
          : day >= today && day <= thisMonthEnd;
        case 'custom': {
          const fromDay = filter.from ? new Date(filter.from + 'T00:00:00') : null;
          const toDay   = filter.to   ? new Date(filter.to   + 'T00:00:00') : null;
          if (fromDay && day < fromDay) return false;
          if (toDay   && day > toDay)   return false;
          return Boolean(filter.from || filter.to);
        }
        default: return true;
      }
    };

    // Same "overdue" definition as the Overdue summary card: deadline passed
    // for open/in-progress tasks, OR completed after its deadline.
    const isTaskOverdue = (t) => {
      if (!t.due_on || t.status === 'cancelled') return false;
      const d = parseServerDate(t.due_on);
      if (!d) return false;
      const due = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      if (t.status === 'done') {
        if (!t.completed_at) return false;
        const c = parseServerDate(t.completed_at);
        if (!c) return false;
        const completedDay = new Date(c.getFullYear(), c.getMonth(), c.getDate());
        return completedDay > due;
      }
      return due < today;
    };

    let result = tasks;
    if (statusFilter.length) result = result.filter(t => statusFilter.includes(t.status));
    if (deadlineFilter.preset === 'overdue') {
      result = result.filter(isTaskOverdue);
    } else if (deadlineFilter.preset) {
      result = result.filter(t => matchesDateFilter(t.due_on, deadlineFilter));
    }
    if (createdFilter.preset)   result = result.filter(t => matchesDateFilter(t.created_at,   createdFilter,   true));
    if (completedFilter.preset) result = result.filter(t => matchesDateFilter(t.completed_at, completedFilter, true));
    if (needSupportFilter) result = result.filter(t => t.review_status === 'need_support');
    if (priorityFilter) result = result.filter(t => t.priority === priorityFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(t =>
        t.title.toLowerCase().includes(q) ||
        (t.task_number || '').toLowerCase().includes(q)
      );
    }
    // Not Started / In Progress first, then Blocked/Cancelled, then Completed last.
    // Within each tier: running timer floats to top, then sort by priority (urgent→high→medium→low)
    result = [...result].sort((a, b) => {
      const aTier = STATUS_SORT_TIER[a.status] ?? 1;
      const bTier = STATUS_SORT_TIER[b.status] ?? 1;
      if (aTier !== bTier) return aTier - bTier;
      const aRunning = a.timer_started_at ? 1 : 0;
      const bRunning = b.timer_started_at ? 1 : 0;
      if (bRunning !== aRunning) return bRunning - aRunning;
      return (PRIORITY_ORDER[a.priority] ?? 4) - (PRIORITY_ORDER[b.priority] ?? 4);
    });
    return result;
  }, [tasks, statusFilter, deadlineFilter, createdFilter, completedFilter, needSupportFilter, priorityFilter, search]);

  const hasActiveFilters = ownerFilter || areaFilter || projectFilter || needSupportFilter || deadlineFilter.preset || createdFilter.preset || completedFilter.preset || priorityFilter || statusFilter.length > 0;

  const activeFilterCount = [
    areaFilter,
    projectFilter,
    needSupportFilter,
    deadlineFilter.preset,
    createdFilter.preset,
    completedFilter.preset,
    priorityFilter,
    ...(isAdmin || hasReportees ? [assigneeFilter && ownerFilter !== 'mine' ? assigneeFilter : ''] : []),
  ].filter(Boolean).length + statusFilter.length;

  // Reset to page 1 whenever filtered results change
  useEffect(() => { setPage(1); }, [filteredTasks]);

  // Completed-only filter has nothing left to bulk-complete — drop out of select mode
  const onlyCompletedFilter = statusFilter.length === 1 && statusFilter[0] === 'done';
  useEffect(() => {
    if (onlyCompletedFilter && selectMode) {
      setSelectMode(false);
      setSelectedIds(new Set());
    }
  }, [onlyCompletedFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.max(1, Math.ceil(filteredTasks.length / PAGE_SIZE));
  const pagedTasks = useMemo(
    () => filteredTasks.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredTasks, page]
  );

  function clearFilters() {
    setOwnerFilter('');
    setAreaFilter('');
    setProjectFilter('');
    setNeedSupportFilter(false);
    setDeadlineFilter(EMPTY_DATE_FILTER);
    setCreatedFilter(EMPTY_DATE_FILTER);
    setCompletedFilter(EMPTY_DATE_FILTER);
    setPriorityFilter('');
    setStatusFilter([]);
  }

  function updateTaskLocally(id, patch) {
    setTasks(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
  }

  const eligibleForBulkSelect = pagedTasks.filter(t =>
    t.status !== 'done' &&
    me && (me.id === t.created_by_user_id || me.id === t.assigned_to_user_id)
  );

  function toggleSelectAll() {
    if (selectMode) {
      setSelectMode(false);
      setSelectedIds(new Set());
    } else {
      setSelectMode(true);
      setSelectedIds(new Set(eligibleForBulkSelect.map(t => t.id)));
      const hasChecklist = eligibleForBulkSelect.some(t => (t.checklist_total || 0) > 0);
      if (hasChecklist) {
        window.alert('Some selected tasks have a checklist. Changing their status will not automatically update checklist items — please review those tasks individually.');
      }
    }
  }

  function toggleRowSelected(taskId) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  }

  async function bulkUpdateStatus(newStatus) {
    if (!selectedIds.size || bulkCompleting) return;
    const selectedTasks = pagedTasks.filter(t => selectedIds.has(t.id));
    const hasChecklist = selectedTasks.some(t => (t.checklist_total || 0) > 0);
    if (newStatus === 'done' && hasChecklist) {
      const proceed = window.confirm('Some selected tasks have a checklist. Completing them will not mark checklist items done. Continue marking them completed?');
      if (!proceed) return;
    }
    setBulkCompleting(true);
    const ids = [...selectedIds];
    try {
      await Promise.all(ids.map(id => api.updateTask(appId, id, { status: newStatus })));
      ids.forEach(id => updateTaskLocally(id, { status: newStatus, completed_at: newStatus === 'done' ? new Date().toISOString() : null }));
    } catch {
      await loadTasks();
    } finally {
      setBulkCompleting(false);
      setSelectMode(false);
      setSelectedIds(new Set());
    }
  }

  return (
    <div className="tasks-page">

      {/* â"€â"€ Page header â"€â"€ */}
      <div className="tl-header">
        <div>
          <h1 className="page-title tl-title-row">
            Tasks
            {overdueCount > 0 && (
              <span className="tl-overdue-badge">{overdueCount} overdue</span>
            )}
          </h1>
          <p className="tl-subtitle">Organize, track and complete your tasks efficiently.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div className="task-view-toggle">
            <button
              className={`task-view-btn${view === 'list' ? ' active' : ''}`}
              onClick={() => setView('list')}
            >
              <List size={14} />
              List View
            </button>
            <button
              className={`task-view-btn${view === 'planner' ? ' active' : ''}`}
              onClick={() => setView('planner')}
            >
              <Calendar size={14} />
              Planner
            </button>
          </div>
          <button className="btn-primary" onClick={() => setCreateOpen(true)}>
            + New Task
          </button>
        </div>
      </div>

      {error && <div className="banner banner-error">{error}</div>}

      {/* ── Summary dashboard ── */}
      <div className="task-dash">
        <div className="task-dash-cards">
          <div className="task-dash-card">
            <span className="task-dash-card-icon task-dash-card-icon-total"><LayoutGrid size={15} /></span>
            <span className="task-dash-card-text">
              <span className="task-dash-card-val">{dashCounts.total}</span>
              <span className="task-dash-card-label">Total</span>
            </span>
          </div>
          <div className="task-dash-card task-dash-card-done">
            <span className="task-dash-card-icon task-dash-card-icon-done"><CheckCircle2 size={15} /></span>
            <span className="task-dash-card-text">
              <span className="task-dash-card-val">{dashCounts.completed}</span>
              <span className="task-dash-card-label">Completed</span>
            </span>
          </div>
          <div className="task-dash-card task-dash-card-progress">
            <span className="task-dash-card-icon task-dash-card-icon-progress"><PlayCircle size={15} /></span>
            <span className="task-dash-card-text">
              <span className="task-dash-card-val">{dashCounts.inProgress}</span>
              <span className="task-dash-card-label">In Progress</span>
            </span>
          </div>
          <div className="task-dash-card task-dash-card-open">
            <span className="task-dash-card-icon task-dash-card-icon-open"><Circle size={15} /></span>
            <span className="task-dash-card-text">
              <span className="task-dash-card-val">{dashCounts.notStarted}</span>
              <span className="task-dash-card-label">Not Started</span>
            </span>
          </div>
          <div className="task-dash-card task-dash-card-overdue">
            <span className="task-dash-card-icon task-dash-card-icon-overdue"><AlertTriangle size={15} /></span>
            <span className="task-dash-card-text">
              <span className="task-dash-card-val">{dashCounts.overdue}</span>
              <span className="task-dash-card-label">Overdue</span>
            </span>
          </div>
        </div>
      </div>

      {/* ── Filter + search bar ── */}
      <div className="tl-toolbar">

        {/* Ownership toggles */}
        <div className="tl-tf-ownership">
          <button
            className={`tl-owner-btn${ownerFilter === 'mine' ? ' active' : ''}`}
            onClick={() => setOwnerFilter(ownerFilter === 'mine' ? '' : 'mine')}
          >
            <User size={13} />
            Mine Only
          </button>
          {isAdmin && (
            <button
              className={`tl-owner-btn${ownerFilter === 'team' ? ' active' : ''}`}
              onClick={() => setOwnerFilter(ownerFilter === 'team' ? '' : 'team')}
            >
              <Users size={13} />
              My Team
            </button>
          )}
        </div>

        <div className="tl-toolbar-sep" />

        {/* Filters button + dropdown panel */}
        <div className="tl-filter-wrap" ref={filterPanelRef}>
          <button
            className={`tl-filter-btn${filterPanelOpen ? ' open' : ''}${activeFilterCount > 0 ? ' has-filters' : ''}`}
            onClick={() => setFilterPanelOpen(o => !o)}
          >
            <SlidersHorizontal size={14} />
            Filters
            {activeFilterCount > 0 && (
              <span className="tl-filter-count">{activeFilterCount}</span>
            )}
            <ChevronDown size={13} className={`tl-filter-chevron${filterPanelOpen ? ' rotated' : ''}`} />
          </button>

          {filterPanelOpen && (
            <div className="tl-filter-panel">
              <div className="tl-fp-grid">

                {/* Status */}
                <div className="tl-fp-field tl-fp-field--full">
                  <label className="tl-fp-label">Status</label>
                  <div className="tl-fp-pills">
                    {STATUS_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        className={`tl-fp-pill${statusFilter.includes(opt.value) ? ' active' : ''}`}
                        style={{ '--pill-dot': opt.dot }}
                        onClick={() => toggleStatusFilter(opt.value)}
                      >
                        <span className="tl-fp-dot" />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Priority */}
                <div className="tl-fp-field tl-fp-field--full">
                  <label className="tl-fp-label">Priority</label>
                  <div className="tl-fp-pills">
                    {PRIORITY_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        className={`tl-fp-pill${priorityFilter === opt.value ? ' active' : ''}`}
                        style={{ '--pill-dot': opt.dot }}
                        onClick={() => setPriorityFilter(priorityFilter === opt.value ? '' : opt.value)}
                      >
                        <span className="tl-fp-dot" />
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Assignee */}
                {(isAdmin || hasReportees) && (
                  <div className="tl-fp-field">
                    <label className="tl-fp-label">Assignee</label>
                    <SearchableSelect
                      placeholder="Any assignee"
                      value={assigneeFilter}
                      onChange={(v) => {
                        setAssigneeFilter(v);
                        if (ownerFilter === 'mine' && v !== String(me?.id)) setOwnerFilter('');
                      }}
                      btnClass="tl-fp-select"
                      options={members
                        .filter(m => isAdmin || !scopeIds || scopeIds.has(String(m.user_id)))
                        .map(m => {
                          const name = [m.first_name, m.last_name].filter(Boolean).join(' ') || m.email;
                          return { value: String(m.user_id), label: name + (me && m.user_id === me.id ? ' (you)' : '') };
                        })}
                    />
                  </div>
                )}

                {/* Area */}
                <div className="tl-fp-field">
                  <label className="tl-fp-label">Area</label>
                  <SearchableSelect
                    placeholder="Any area"
                    value={areaFilter}
                    onChange={setAreaFilter}
                    btnClass="tl-fp-select"
                    options={[{ value: 'others', label: 'Internal (no area)' }]}
                    groups={roles
                      .map(r => ({ label: r.title, options: (areasByRole.get(r.id) || []).map(a => ({ value: a.id, label: a.name })) }))
                      .filter(g => g.options.length)}
                  />
                </div>

                {/* Project */}
                <div className="tl-fp-field">
                  <label className="tl-fp-label">Project</label>
                  <SearchableSelect
                    placeholder="Any project"
                    value={projectFilter}
                    onChange={setProjectFilter}
                    btnClass="tl-fp-select"
                    options={projects.map(p => ({ value: p.id, label: p.name + (p.is_deleted ? ' (Deleted)' : '') }))}
                  />
                </div>

                {/* Deadline */}
                <div className="tl-fp-field">
                  <label className="tl-fp-label">Deadline</label>
                  <DateRangeFilterDropdown
                    label="Any deadline"
                    value={deadlineFilter}
                    onChange={setDeadlineFilter}
                    btnClass="tl-fp-select"
                  />
                </div>

                {/* Created */}
                <div className="tl-fp-field">
                  <label className="tl-fp-label">Created</label>
                  <DateRangeFilterDropdown
                    label="Any date"
                    value={createdFilter}
                    onChange={setCreatedFilter}
                    btnClass="tl-fp-select"
                    presets={DATE_PRESETS_PAST}
                  />
                </div>

                {/* Completed */}
                <div className="tl-fp-field">
                  <label className="tl-fp-label">Completed</label>
                  <DateRangeFilterDropdown
                    label="Any date"
                    value={completedFilter}
                    onChange={setCompletedFilter}
                    btnClass="tl-fp-select"
                    presets={DATE_PRESETS_PAST}
                  />
                </div>

                {/* Need Support */}
                <div className="tl-fp-field tl-fp-field--inline">
                  <label className="tl-fp-label">Need Support</label>
                  <button
                    className={`tl-fp-toggle${needSupportFilter ? ' active' : ''}`}
                    onClick={() => setNeedSupportFilter(f => !f)}
                  >
                    {needSupportFilter ? 'On' : 'Off'}
                  </button>
                </div>

              </div>

              {/* Panel footer */}
              <div className="tl-fp-footer">
                {hasActiveFilters && (
                  <button className="tl-fp-reset" onClick={() => { clearFilters(); setFilterPanelOpen(false); }}>
                    Reset all
                  </button>
                )}
                <button className="tl-fp-apply" onClick={() => setFilterPanelOpen(false)}>
                  Done
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── Active filter chips (inside toolbar) ── */}
        {hasActiveFilters && (() => {
          const chips = [];

          statusFilter.forEach(v => chips.push({
            key: 'status-' + v,
            label: STATUS_LABEL[v] || v,
            clear: () => setStatusFilter(statusFilter.filter(s => s !== v)),
          }));
          if (priorityFilter) chips.push({
            key: 'priority',
            label: 'Priority: ' + (PRIORITY_OPTIONS.find(o => o.value === priorityFilter)?.label || priorityFilter),
            clear: () => setPriorityFilter(''),
          });
          if (assigneeFilter && ownerFilter !== 'mine') chips.push({
            key: 'assignee',
            label: 'Assignee: ' + (memberName(Number(assigneeFilter)) || `#${assigneeFilter}`),
            clear: () => setAssigneeFilter(''),
          });
          if (areaFilter) chips.push({
            key: 'area',
            label: 'Area: ' + (areaFilter === 'others' ? 'Internal' : areaLabel(Number(areaFilter))),
            clear: () => setAreaFilter(''),
          });
          if (projectFilter) chips.push({
            key: 'project',
            label: 'Project: ' + (projects.find(p => String(p.id) === String(projectFilter))?.name || `#${projectFilter}`),
            clear: () => setProjectFilter(''),
          });
          if (deadlineFilter.preset) chips.push({
            key: 'deadline',
            label: 'Deadline: ' + (deadlineFilter.preset === 'custom' ? `${deadlineFilter.from || '…'} – ${deadlineFilter.to || '…'}` : deadlineFilter.preset.replace(/_/g, ' ')),
            clear: () => setDeadlineFilter(EMPTY_DATE_FILTER),
          });
          if (createdFilter.preset) chips.push({
            key: 'created',
            label: 'Created: ' + (createdFilter.preset === 'custom' ? `${createdFilter.from || '…'} – ${createdFilter.to || '…'}` : createdFilter.preset.replace(/_/g, ' ')),
            clear: () => setCreatedFilter(EMPTY_DATE_FILTER),
          });
          if (completedFilter.preset) chips.push({
            key: 'completed',
            label: 'Completed: ' + (completedFilter.preset === 'custom' ? `${completedFilter.from || '…'} – ${completedFilter.to || '…'}` : completedFilter.preset.replace(/_/g, ' ')),
            clear: () => setCompletedFilter(EMPTY_DATE_FILTER),
          });
          if (needSupportFilter) chips.push({
            key: 'support',
            label: 'Need Support',
            clear: () => setNeedSupportFilter(false),
          });
          if (ownerFilter === 'mine') chips.push({
            key: 'owner',
            label: 'Mine Only',
            clear: () => setOwnerFilter(''),
          });
          if (ownerFilter === 'team') chips.push({
            key: 'owner',
            label: 'My Team',
            clear: () => setOwnerFilter(''),
          });

          return (
            <>
              <div className="tl-toolbar-sep" />
              <div className="tl-active-chips">
                {chips.map(chip => (
                  <span key={chip.key} className="tl-active-chip" title={chip.label}>
                    <span className="tl-active-chip-label">{chip.label}</span>
                    <button className="tl-active-chip-x" onClick={chip.clear} title="Remove filter">×</button>
                  </span>
                ))}
                <button className="tl-active-chips-reset" onClick={clearFilters}>Clear all</button>
              </div>
            </>
          );
        })()}
      </div>

      {/* â"€â"€ Planner (Gantt) view â"€â"€ */}
      {view === 'planner' && !loading && (
        <TaskGantt
          tasks={tasks}
          appId={appId}
          members={members}
          me={me}
          statusFilter={statusFilter}
          search={search}
          assigneeUserId={assigneeFilter}
        />
      )}

      {/* â"€â"€ Task list â"€â"€ */}
      {view === 'list' && (loading ? (
        <div className="tl-loading">
          <span className="tl-spinner" />
          Loading tasks...
        </div>
      ) : filteredTasks.length === 0 ? (
        <div className="tl-empty">
          <div className="tl-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.3 }}>
              <rect x="9" y="2" width="6" height="4" rx="1"/>
              <rect x="3" y="5" width="18" height="17" rx="2"/>
              <line x1="8" y1="11" x2="16" y2="11"/>
              <line x1="8" y1="15" x2="13" y2="15"/>
            </svg>
          </div>
          <p className="tl-empty-title">
            {search
              ? `No tasks match "${search}"`
              : statusFilter.length === 1
                ? `No ${STATUS_LABEL[statusFilter[0]]?.toLowerCase()} tasks`
                : statusFilter.length > 1
                  ? 'No tasks match the selected statuses'
                  : 'No tasks yet'}
          </p>
          <p className="tl-empty-sub">
            {search || statusFilter.length
              ? 'Try adjusting your filters.'
              : 'Create your first task to get started.'}
          </p>
          {!search && !statusFilter.length && (
            <button className="btn-primary" style={{ marginTop: 12 }} onClick={() => setCreateOpen(true)}>
              + New Task
            </button>
          )}
        </div>
      ) : (
        <>
        {!onlyCompletedFilter && (
        <div className="tl-bulk-bar">
          <label className="tl-bulk-select-all">
            <input
              type="checkbox"
              className="tl-checkbox"
              checked={selectMode}
              onChange={toggleSelectAll}
            />
            Select All
          </label>
          {selectMode && (
            <div className="tl-bulk-actions">
              <select
                className="tl-bulk-status-select"
                value={bulkStatus}
                onChange={e => setBulkStatus(e.target.value)}
                disabled={bulkCompleting}
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <button
                className="btn-primary tl-bulk-complete-btn"
                disabled={!selectedIds.size || bulkCompleting}
                onClick={() => bulkUpdateStatus(bulkStatus)}
              >
                {bulkCompleting ? 'Updating…' : `Update Status (${selectedIds.size})`}
              </button>
            </div>
          )}
        </div>
        )}
        {totalPages > 1 && (
          <div className="tl-pagination tl-pagination--top">
            <span className="tl-page-info">
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filteredTasks.length)} of {filteredTasks.length} tasks
            </span>
            <div className="tl-page-btns">
              <button className="tl-page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                .reduce((acc, n, i, arr) => {
                  if (i > 0 && n - arr[i - 1] > 1) acc.push('…');
                  acc.push(n);
                  return acc;
                }, [])
                .map((n, i) =>
                  n === '…'
                    ? <span key={`ellipsis-${i}`} className="tl-page-ellipsis">…</span>
                    : <button key={n} className={`tl-page-num${n === page ? ' active' : ''}`} onClick={() => setPage(n)}>{n}</button>
                )
              }
              <button className="tl-page-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          </div>
        )}
        <div className="tl-list">
          {pagedTasks.map((t, idx) => {
            const chkTotal  = t.checklist_total || 0;
            const chkDone   = t.checklist_done  || 0;
            const chkPct    = chkTotal > 0 ? Math.round((chkDone / chkTotal) * 100) : null;
            const assignee  = memberName(t.assigned_to_user_id);
            const assigneePhoto = memberPhoto(t.assigned_to_user_id);
            const isMe      = me && t.assigned_to_user_id === me.id;
            const canEditTask = !['done', 'blocked', 'cancelled'].includes(t.status)
              && (isAdmin || (me && (me.id === t.created_by_user_id || me.id === t.assigned_to_user_id)));
            const dueLabel  = smartDueLabel(t.due_on, t.status, t.completed_at);
            const dueClass  = dueDateClass(t.due_on, t.status);
            const isDimmed  = t.status === 'done' || t.status === 'cancelled';

            // â"€â"€ Time budget â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€
            const loggedH    = parseFloat(t.total_hours) || 0;
            const estMins    = t.estimated_minutes || 0;
            const estH       = estMins > 0 ? estMins / 60 : null;
            const timePct    = estH ? Math.round((loggedH / estH) * 100) : null;
            const timeOver   = timePct !== null && timePct > 100;
            const timeWarn   = timePct !== null && timePct >= 80 && timePct <= 100;
            const timeBarW   = timePct !== null ? Math.min(timePct, 100) : 0;
            const timeBarClr = timeOver ? '#dc2626' : timeWarn ? '#d97706' : '#166534';
            const loggedMins = parseFloat(t.total_minutes) || Math.round(loggedH * 60);
            const timeText   = estMins > 0
              ? `${formatHoursMinutes(loggedMins)}/${formatHoursMinutes(estMins)}`
              : `${formatHoursMinutes(loggedMins)}`;
            const showTime   = t.total_minutes > 0 || estMins > 0;
            const timerRunning = !!t.timer_started_at;

            return (
              <div
                key={t.id}
                className={`tl-row tl-priority-${t.priority}${timerRunning ? ' tl-row-timer-running' : ''}`}
                onClick={() => {
                  _filterCache = { appId, ownerFilter, areaFilter, assigneeFilter, needSupportFilter, deadlineFilter, createdFilter, completedFilter, projectFilter, priorityFilter, view };
                  navigate(`/apps/${appId}/tasks/${t.id}`);
                }}
              >
                {/* SL No */}
                <div className="tl-row-sl">{(page - 1) * PAGE_SIZE + idx + 1}</div>

                {/* Checkbox — only shown in bulk-select mode; checking + "Mark Completed" completes selected tasks */}
                <div className="tl-row-check" onClick={(e) => e.stopPropagation()}>
                  {selectMode && (
                    <input
                      type="checkbox"
                      className="tl-checkbox"
                      checked={selectedIds.has(t.id)}
                      disabled={
                        t.status === 'done' ||
                        (me?.id !== t.created_by_user_id && me?.id !== t.assigned_to_user_id)
                      }
                      onChange={() => toggleRowSelected(t.id)}
                    />
                  )}
                </div>

                {/* Task number */}
                <div className="tl-row-num">
                  <span
                    className="task-number-badge small"
                    title="Click to copy"
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      const url = `${window.location.origin}/apps/${appId}/tasks/${t.id}`;
                      navigator.clipboard.writeText(url);
                      const el = e.currentTarget;
                      const orig = el.textContent;
                      el.textContent = 'Copied!';
                      setTimeout(() => { el.textContent = orig; }, 1200);
                    }}
                  >
                    {t.task_number || `#${t.id}`}
                  </span>
                </div>

                {/* Main content */}
                <div className="tl-row-body">
                  <div className="tl-row-title-line">
                    <span className={`tl-row-title ${isDimmed ? 'tl-row-title--dim' : ''}`}>
                      {t.title}
                    </span>
                    <span className={`status-pill status-${t.status}`}>
                      {STATUS_LABEL[t.status] || t.status}
                    </span>
                    <span className={`priority-pill priority-${t.priority === 'critical' ? 'urgent' : t.priority}`}>
                      {PRIORITY_LABEL[t.priority] || t.priority}
                    </span>
                    <TaskSourceTag task={t} />
                  </div>

                  {(t.area_id !== undefined || chkTotal > 0 || t.comment_count > 0 || showTime || t.due_on) && (
                    <div className="tl-row-chips">
                      {t.area_id !== undefined && (
                        <span className="tl-chip tl-chip-area" title={t.project_name || t.agreement_name || t.agreement_number || areaLabel(t.area_id)}>
                          {t.project_name
                            ? <FolderOpen size={12} className="tl-chip-icon" />
                            : t.agreement_name || t.agreement_number
                              ? <FileText size={12} className="tl-chip-icon" />
                              : <svg viewBox="0 0 12 12" fill="none" className="tl-chip-icon">
                                  <path d="M2 2h8v8H2z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
                                  <path d="M5 2v8" stroke="currentColor" strokeWidth="1.2"/>
                                </svg>
                          }
                          <span className="tl-chip-text">
                            {t.project_name || t.agreement_name || t.agreement_number || areaLabel(t.area_id)}
                          </span>
                        </span>
                      )}

                      {/* Absolute date chip */}
                      {t.due_on && (
                        <span
                          className="tl-chip"
                          style={
                            dueClass === 'tl-due-overdue' ? { color: '#842029', fontWeight: 600 }
                            : dueClass === 'tl-due-soon'  ? { color: '#856404', fontWeight: 600 }
                            : undefined
                          }
                        >
                          <Calendar size={12} className="tl-chip-icon" style={dueClass ? { color: 'inherit' } : {}} />
                          {formatDate(t.due_on)}
                        </span>
                      )}
                      {chkTotal > 0 && (
                        <span className="tl-chip tl-chip-checklist">
                          <span className="tl-mini-track">
                            <span
                              className="tl-mini-fill"
                              style={{
                                width: `${chkPct}%`,
                                background: chkPct === 100 ? '#166534' : '#1a472a',
                              }}
                            />
                          </span>
                          <span>{chkDone}/{chkTotal}</span>
                        </span>
                      )}
                      {t.comment_count > 0 && (
                        <span className="tl-chip">
                          <svg viewBox="0 0 12 12" fill="none" className="tl-chip-icon">
                            <path d="M10 1H2a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h1v2l3-2h4a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
                          </svg>
                          {t.comment_count}
                        </span>
                      )}
                      {/* â"€â"€ Time budget chip â"€â"€ */}
                      {showTime && (
                        <span className={`tl-chip tl-chip-time${timeOver ? ' tl-chip-over' : timeWarn ? ' tl-chip-warn' : ''}`}>
                          <svg viewBox="0 0 12 12" fill="none" className="tl-chip-icon">
                            <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.2"/>
                            <path d="M6 3.5V6l2 1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
                          </svg>
                          {estH !== null && (
                            <span className="tl-mini-track">
                              <span
                                className="tl-mini-fill"
                                style={{ width: `${timeBarW}%`, background: timeBarClr }}
                              />
                            </span>
                          )}
                          <span>{timeText}</span>
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Assignee: avatar + name */}
                <div className="tl-row-assignee" title={assignee || 'Unassigned'}>
                  {assignee ? (
                    <span className={`tl-avatar ${isMe ? 'tl-avatar--me' : ''}`}>
                      {assigneePhoto
                        ? <img src={assigneePhoto} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                        : nameInitials(assignee)}
                    </span>
                  ) : (
                    <span className="tl-avatar tl-avatar--empty">{'—'}</span>
                  )}
                  <span className="tl-row-assignee-name">{assignee || 'Unassigned'}</span>
                </div>

                {/* Right: due date + edit */}
                <div className="tl-row-right">
                  {dueLabel && (
                    <span className={`tl-due ${dueClass}`}>{dueLabel}</span>
                  )}
                  {canEditTask && (
                    <button
                      className="tl-edit-btn"
                      title="Edit task"
                      onClick={e => { e.stopPropagation(); setEditTask(t); }}
                    >
                      <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                        <path d="M9 2L11 4L5 10H3V8L9 2Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        </>
      ))}


      {view === 'list' && !loading && search && filteredTasks.length > 0 && filteredTasks.length < tasks.length && (
        <p className="muted small" style={{ marginTop: 10 }}>
          Showing {filteredTasks.length} of {tasks.length} tasks
        </p>
      )}

      <CreateTaskModal
        open={createOpen || !!editTask}
        onClose={() => { setCreateOpen(false); setEditTask(null); }}
        members={members}
        roles={roles}
        areasByRole={areasByRole}
        onCreated={async () => { await loadTasks(); }}
        editTask={editTask}
        onUpdated={async () => { await loadTasks(); }}
      />
    </div>
  );
}

